/**
 * Manage displaying proxy controls for interactive.
 *
 * This module seems plugin's agnostic, so it can be moved to the separate package.
 */

/**
 * Use them as "unique" class name for each instance of the Control
 * @type {string}
 */
const randomHash = Date.now().toString(36);

class Control {
  #element;
  #description;

  #timeoutId;

  constructor(controlDescription, events) {
    this.#element = document.createElement(controlDescription.element_type);
    this.#element.id = controlDescription.element_id;
    this.#element.style.visibility = 'hidden';
    /*
    this.#element.style.width = controlDescription.element_position.width + 'px';
    this.#element.style.height = controlDescription.element_position.height + 'px';
    this.#element.style.top = controlDescription.element_position.top + 'px';
    this.#element.style.left = controlDescription.element_position.left + 'px';
    */
    this.#element.classList.add(randomHash); /* for all other, but static styles */

    Object.entries(controlDescription.attributes ?? {})
      .forEach(([key, value]) => {
        this.#element[key] = value;
      });

    this.#description = controlDescription;
  }

  appendTo(container) {
    if (!container) {
      return;
    }
    container.appendChild(this.#element);
  }

  destroy(){
    this.#element.parentElement?.removeChild?.(this.#element);
  }
  /**
   *
   * @param {ElementPosition} elementPosition
   */
  show(elementPosition) {
    clearTimeout(this.#timeoutId);
    /*
    this.#element.style.top = elementPosition.top + 'px';
    this.#element.style.left = elementPosition.left + 'px';
    this.#element.style.width = elementPosition.width + 'px';
    this.#element.style.height = elementPosition.height + 'px';
    */
    Object.assign(this.#element.style, {
      top: elementPosition.top + 'px',
      left: elementPosition.left + 'px',
      width: elementPosition.width + 'px',
      height: elementPosition.height + 'px',
    });
    this.#element.style.visibility = 'visible';
    // this.#element.style.willChange = 'transform';
    // this.#element.style.transform = `translate(${elementPosition.left}px, ${elementPosition.top}px)`;
    // this.#element.style.willChange = 'none';
    // this.#element.style.display = 'initial';
  }

  hide(cb) {
    this.#element.style.visibility = 'hidden';
    // this.#element.style.display = 'none';
    clearTimeout(this.#timeoutId);
    this.#timeoutId = setTimeout(() => {
      cb();
    }, 1_000);
  }
}

export default class ControlsManager {
  /**
   * @type {Map<string, HTMLElement>}
   */
  #containers = new Map();

  /**
   * @type {Map<string, Control>}
   */
  #controls = new Map();

  #listeners = new Set();

  constructor() {
    const styleElement = document.createElement('style');
    styleElement.id = `mggp-interactive-controls-${randomHash}`;
    styleElement.innerHTML = `
.${randomHash} {
position: absolute;
opacity: 0;
pointer-events: all;
cursor: pointer;
background: none;
border: none;
padding: 0;
}`;

      document.head.appendChild(styleElement);
  }
  addContainer(container) {
    if (!container) {
      return;
    }

    container.addEventListener('click', this.#onEvent);

    container.addEventListener('input', this.#onEvent);

    container.addEventListener('focusin', this.#onFocus);
    container.addEventListener('focusout', this.#onBlur);

    container.addEventListener('pointerover', this.#onOver);
    container.addEventListener('pointerout', this.#onLeave);

    container.addEventListener('pointerdown', this.#onEvent);
    container.addEventListener('pointerup', this.#onEvent);

    this.#containers.set(container.id, container);

    return () => {
      this.#containers.delete(container.id);
      container.removeEventListener('click', this.#onEvent);
      container.removeEventListener('input', this.#onEvent);
      container.removeEventListener('focusin', this.#onFocus);
      container.removeEventListener('focusout', this.#onBlur);
      container.removeEventListener('pointerover', this.#onOver);
      container.removeEventListener('pointerout', this.#onLeave);
      container.removeEventListener('pointerdown', this.#onEvent);
      container.removeEventListener('pointerup', this.#onEvent);
    };
  }

  onEvents(handler) {
    this.#listeners.add(handler);
    return () => this.#listeners.delete(handler);
  };

  #onEvent = (event) => {
    const element_id = event.target.id;
    const control = this.#controls.get(element_id);
    if (!control) {
      return;
    }

    const payload = {
      event_type: event.type,
      element_id,
      event: {
        type: event.type,
        target: {
          id: event.target.id,
          value: event.target.value,
          /*
            Extend this object with other properties on demand
           */
        }
      }
    };

    this.#listeners.forEach((handler) => handler(payload));
  };

  #onFocus = event => {
    this.#onEvent({
      type: 'focus',
      target: event.target,
    });
  };

  #onBlur = event => {
    this.#onEvent({
      type: 'blur',
      target: event.target,
    });
  };

  #onOver = event => {
    this.#onEvent({
      type: 'over',
      target: event.target,
    });
  };

  #onLeave = event => {
    this.#onEvent({
      type: 'leave',
      target: event.target,
    });
  };

  #getContainer(containerId) {
    let container = this.#containers.get(containerId);
    if (!container) {
      container = document.getElementById(containerId);
      if (!container) {
        return;
      }
      this.addContainer(container);

    }
    return container;
  }
  /**
   *
   * @param {InteractiveElement} controlDescription
   * @param {InteractiveElementEventLists} events
   */
  showControl(controlDescription, events) {
    const container = this.#getContainer(controlDescription.zone_id);

    if (!container) {
      return;
    }

    let control = this.#controls.get(controlDescription.element_id);

    if (!control) {
      control = new Control(controlDescription, event);
      control.appendTo(container);
      this.#controls.set(controlDescription.element_id, control);
    }

    control.show(controlDescription.element_position);
  }

  /**
   * @param {InteractiveElement} controlDescription
   */
  hideControl(controlDescription) {
    const control = this.#controls.get(controlDescription.element_id);
    if (!control) {
      return;
    }

    control.hide(() => {
      control.destroy();
      this.#controls.delete(controlDescription.element_id);
    });
  }
}

function throttle(fn) {
  let isThrottled = false;
  return function (...args) {
    if (isThrottled) {
      return;
    }
    isThrottled = true;
    fn(...args);
    setTimeout(() => {
      isThrottled = false;
    }, 100);
  };
}
